import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { Api } from 'common/api/api'
import { apiConfig } from 'common/api/api.config'
import GenericPaginationParams from 'common/types/genericPaginationParams'
import { PaginatedResponse } from 'common/types/managers'
import {
  Notification,
  ChangeNotificationData,
  NotificationPaginatedResponse,
  NotificationKind,
  NotificationsApp,
  NotificationScope,
  NotificationsParams,
  MessagePreview,
  MessageRecipients,
  MessageThread,
  RecipientSearchResult,
  SendMessageData,
  SentMessage,
  SentMessageDetail,
  CreateNotificationData,
  GeneralNotification
} from 'common/types/notifications'

const ENDPOINT = '/notifications'
const GENERALENDPOINT = '/generalNotifications'

class NotificationsApi extends Api {
  public constructor(config: AxiosRequestConfig) {
    super(config)

    // this middleware is been called right before the http request is made.
    this.interceptors.request.use((param: AxiosRequestConfig) => ({
      ...param
    }))

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use((param: AxiosResponse) => ({
      ...param
    }))

    // to use in sagas
    Object.setPrototypeOf(this, NotificationsApi.prototype)
  }

  public async getNotifications(
    params?: NotificationsParams
  ): Promise<NotificationPaginatedResponse<Notification>> {
    try {
      const res: AxiosResponse<NotificationPaginatedResponse<Notification>> = await this.get<
        Notification,
        AxiosResponse<NotificationPaginatedResponse<Notification>>
      >(`${ENDPOINT}/`, { params: { ...params } })

      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async markAllNotificationsAsRead(
    scope: NotificationScope,
    kind?: NotificationKind
  ): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.patch(
        `${ENDPOINT}/markAllNotificationsAsRead/`,
        undefined,
        { params: { scope, kind } }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async changeNotificationReadDate(data: ChangeNotificationData): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.post(
        `${ENDPOINT}/changeNotificationReadDate/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async deleteNotification(id: number): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.delete(`${ENDPOINT}/${id}/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async markNotificationSeen(id: number): Promise<Notification> {
    const res: AxiosResponse<Notification> = await this.post(`${ENDPOINT}/${id}/seen/`)
    return this.success(res)
  }
  public async searchRecipients(q: string): Promise<RecipientSearchResult> {
    const res: AxiosResponse<RecipientSearchResult> = await this.get(`${ENDPOINT}/recipients/`, {
      params: { q }
    })
    return this.success(res)
  }
  public async previewMessage(data: MessageRecipients): Promise<MessagePreview> {
    const res: AxiosResponse<MessagePreview> = await this.post(`${ENDPOINT}/messagePreview/`, data)
    return this.success(res)
  }
  public async sendMessage(data: SendMessageData): Promise<{ id: number; recipients: number }> {
    const res: AxiosResponse<{ id: number; recipients: number }> = await this.post(
      `${ENDPOINT}/sendMessage/`,
      data
    )
    return this.success(res)
  }
  public async getSentMessages(params: {
    page: number
    page_size: number
  }): Promise<PaginatedResponse<SentMessage>> {
    const res: AxiosResponse<
      PaginatedResponse<SentMessage>
    > = await this.get(`${ENDPOINT}/sentMessages/`, { params })
    return this.success(res)
  }
  public async getSentMessage(id: number): Promise<SentMessageDetail> {
    const res: AxiosResponse<SentMessageDetail> = await this.get(`${ENDPOINT}/sentMessage/`, {
      params: { id }
    })
    return this.success(res)
  }

  public async getMessageThread(id: number): Promise<MessageThread> {
    const res: AxiosResponse<MessageThread> = await this.get(`${ENDPOINT}/messageThread/`, {
      params: { id }
    })
    return this.success(res)
  }
  public async replyMessage(data: { message_id: number; body: string; to?: number }) {
    const res: AxiosResponse<{ id: number }> = await this.post(`${ENDPOINT}/replyMessage/`, data)
    return this.success(res)
  }
  public async readMessageThread(id: number): Promise<boolean> {
    const res: AxiosResponse<boolean> = await this.post(`${ENDPOINT}/readMessageThread/`, { id })
    return this.success(res)
  }

  // Notifications App/Infrastructure
  public async createAppNotification(data: CreateNotificationData): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.post(`${GENERALENDPOINT}/`, data, {
        headers: { 'Content-Type': 'application/json' }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async updateAppNotification(data: NotificationsApp): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.patch(`${GENERALENDPOINT}/${data.id}/`, data, {
        headers: { 'Content-Type': 'application/json' }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async deleteAppNotification(id: string): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.delete(`${GENERALENDPOINT}/${id}/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getAllAppNotifications(
    params?: GenericPaginationParams
  ): Promise<PaginatedResponse<NotificationsApp>> {
    try {
      const res: AxiosResponse<PaginatedResponse<NotificationsApp>> = await this.get<
        NotificationsApp,
        AxiosResponse<PaginatedResponse<NotificationsApp>>
      >(`${GENERALENDPOINT}/`, { params: { ...params } })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getAppNotification(id: string): Promise<NotificationsApp> {
    try {
      const res: AxiosResponse<NotificationsApp> = await this.get<
        NotificationsApp,
        AxiosResponse<NotificationsApp>
      >(`${GENERALENDPOINT}/${id}/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async generalNotificationsHome(
    params?: GenericPaginationParams
  ): Promise<NotificationPaginatedResponse<GeneralNotification>> {
    try {
      const res: AxiosResponse<NotificationPaginatedResponse<GeneralNotification>> = await this.get<
        GeneralNotification,
        AxiosResponse<NotificationPaginatedResponse<GeneralNotification>>
      >(`${GENERALENDPOINT}/generalNotificationsHome/`, { params: { ...params } })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async markGeneralNotificationAsRead(generalNotificationId: number): Promise<boolean> {
    try {
      const data = { general_notification_id: generalNotificationId }
      const res: AxiosResponse<boolean> = await this.post(
        `${GENERALENDPOINT}/markGeneralNotificationAsRead/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async markAllGeneralNotificationsAsRead(): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.patch(
        `${GENERALENDPOINT}/markAllGeneralNotificationsAsRead/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
}

export const notificationsApi = new NotificationsApi(apiConfig)
